import React, { useState, useEffect, useRef } from "react";
import {
  useAdminLoginMsMutation,
  useEnable2faMutation,
  useCheck2faCodeMutation,
  useGetAdminConfigQuery
} from "../../app/api";
import {
  saveAdminCredentials
} from "./authSlice";
import LoginError from "./LoginError";
import {
  Alert,
  Button,
  InputGroup,
  Form
} from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../msalConfig';
import { useLocation, useNavigate } from 'react-router-dom';



export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const incomingState = location.state || {};

  // Admin-configurable visibility of the external sign-in links (Super Admin →
  // Config). Defaults to showing both until the flags load / if the call fails.
  const { data: adminConfig } = useGetAdminConfigQuery();
  const showClientPortalLink = adminConfig?.config?.show_client_portal_link !== false;
  const showEntrepreneurPortalLink = adminConfig?.config?.show_entrepreneur_portal_link !== false;

  const [username] = useState(incomingState.email || "");
  const [errorStatusCode, setErrorStatusCode] = useState(incomingState.errorStatusCode || undefined);
  const [isError, setIsError] = useState(!!incomingState.errorStatusCode);
  const [show2FA, setShow2FA] = useState(incomingState.need2FA && incomingState.setup ? true : false);
  const [qrCode, setQrCode] = useState(null);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);
  const [invalid2FA, setInvalid2FA] = useState(false);
  const [show2FAlogin, setShow2FAlogin] = useState(incomingState.need2FA && !incomingState.setup ? true : false);
  const [msIdToken] = useState(incomingState.idToken || null);

  const [loginMs, { isLoading }] = useAdminLoginMsMutation();
  const [check2FA] = useCheck2faCodeMutation();
  const [start2FA] = useEnable2faMutation();
  const { instance: msalInstance } = useMsal();
  
  
  const canLogin = !isLoading;

  async function onLogin(email, idToken, code = '') {
    console.log('onLogin');
    setErrorStatusCode(undefined);
    setIsError(false);
    try {
      const result = await loginMs({ email, id_token: idToken, code }).unwrap();
      if (result.permissions && result.permissions[0] === 'PENDING') {
        setErrorStatusCode(222);
      } else {
        saveAdminCredentials(result.access_token, result.uuid, email);
      }
    } catch (err) {
      if (err && err.status === 511) {
        setShow2FA(true);
      } else if (err && err.status === 424) {
        setShow2FAlogin(true);
      } else {
        setErrorStatusCode(err.status);
      }
    }
  }

  const loginWithMicrosoft = () => {
    console.log('loginWithMicrosoft');
    setIsError(false);
    msalInstance.loginRedirect(loginRequest).catch((err) => {
      console.error('MSAL redirect error:', err);
      setIsError(true);
      setErrorStatusCode(223);
    });
  };

  const handle2FACodeCheck = async (code) => {
    console.log('handle2FACodeCheck');
    const res = await check2FA({'username': username, 'code': code});
    if (res.data === 'INVALID') { setInvalid2FA(true); }
    if (res.data === 'OK') { onLogin(username, msIdToken, code); }
  };

  const enable2FA = async () => {
    console.log('enable2FA');
    const res = await start2FA({'username': username});
    if (res.data?.qr_code) {
        setQrCode(res.data);
    }
  }

  useEffect(() => {
    if ((show2FA && qrCode) || show2FAlogin) {
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    }
  }, [show2FA, qrCode, show2FAlogin]);

  const handle2FACODEChange = (index, value) => {
    console.log('handle2FACODEChange');
    setInvalid2FA(false);
    if (!/^\d*$/.test(value)) return; // only numbers
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    // Auto jump to next
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    // Get full code when complete
    if (newCode.every(digit => digit)) {
      const fullCode = newCode.join('');
      handle2FACodeCheck(fullCode);
    }
  }

  const handle2FAKeyDown = (index, e) => {
    console.log('handle2FAKeyDown');
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }


  const [copied, setCopied] = useState(false);

  const handleCopy = (data) => {
    console.log('handleCopy');
    navigator.clipboard.writeText(data);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const brand = {
    deepBlue:    '#0a2947',
    tealBlue:    '#0069a6',
    goldenrod:   '#d09b2c',
    charcoal:    '#58585b',
    softGray:    '#e9e6e1',
    tertiaryGray:'#9d9fa1',
  };

  const pageStyle = {
    minHeight: '100vh',
    backgroundImage: `url(/homepage-hero-overlay.jpg)`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: brand.deepBlue,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
    position: 'relative',
  };

  const topBannerStyle = {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    backgroundColor: brand.goldenrod,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 20px',
    color: brand.deepBlue,
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  };

  const leftPaneStyle = {
    position: 'relative',
    zIndex: 1,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
  };

  const overlayStyle = {
    position: 'absolute',
    inset: 0,
    background: `linear-gradient(to right,
      rgba(10,41,71,0.95) 0%,
      rgba(10,41,71,0.80) 38%,
      rgba(10,41,71,0.35) 58%,
      rgba(10,41,71,0.35) 72%,
      rgba(10,41,71,0.80) 88%,
      rgba(10,41,71,0.95) 100%
    )`,
  };

  const paneLogoStyle = {
    position: 'relative',
    zIndex: 1,
    textAlign: 'center',
    marginBottom: '28px',
    width: '100%',
    maxWidth: '420px',
  };

  const logoWordmarkStyle = {
    color: brand.deepBlue,
    fontWeight: '900',
    fontSize: '24px',
    letterSpacing: '3px',
    textTransform: 'uppercase',
    margin: 0,
    lineHeight: 1.1,
  };

  const logoSubStyle = {
    color: brand.tealBlue,
    fontWeight: '700',
    fontSize: '11px',
    letterSpacing: '4px',
    textTransform: 'uppercase',
    marginTop: '4px',
    marginBottom: 0,
  };

  const logoDividerStyle = {
    borderTop: `2px solid ${brand.goldenrod}`,
    width: '120px',
    margin: '10px auto 8px',
    opacity: 1,
  };

  const taglineStyle = {
    color: brand.charcoal,
    fontSize: '11px',
    letterSpacing: '1px',
    margin: 0,
    opacity: 0.75,
    fontStyle: 'italic',
  };

  const cardStyle = {
    position: 'relative',
    zIndex: 1,
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 8px 40px rgba(10,41,71,0.6)',
    width: '100%',
    maxWidth: '420px',
    overflow: 'hidden',
  };

  const cardBodyStyle = {
    padding: '32px 40px 36px',
    textAlign: 'center',
  };


  const googleBtnStyle = {
    backgroundColor: brand.tealBlue,
    color: '#ffffff',
    fontWeight: '700',
    letterSpacing: '1px',
    padding: '12px',
    fontSize: '15px',
    borderRadius: '4px',
    width: '100%',
    border: 'none',
    cursor: 'pointer',
  };

  const footerLinkStyle = {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.85)',
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    cursor: 'pointer',
    padding: 0,
    transition: 'color 0.15s',
  };

  const codeInputStyle = {
    width: '52px',
    height: '64px',
    fontSize: '28px',
    fontWeight: '700',
    borderRadius: '6px',
    border: `2px solid ${brand.tertiaryGray}`,
    textAlign: 'center',
    color: brand.deepBlue,
    outline: 'none',
  };

  const TwoFAInputs = () => (
    <div className="d-flex gap-2 justify-content-center mt-3">
      {code.map((digit, index) => (
        <input
          key={index}
          ref={el => inputRefs.current[index] = el}
          type="text"
          maxLength="1"
          value={digit}
          onChange={(e) => handle2FACODEChange(index, e.target.value)}
          onKeyDown={(e) => handle2FAKeyDown(index, e)}
          style={codeInputStyle}
        />
      ))}
    </div>
  );

  return (
    <div style={pageStyle}>
      <div style={overlayStyle} />

      {/* Full-width top banner */}
      <div style={topBannerStyle}>
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
          <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
        </svg>
        Internal Staff Portal — Authorized Access Only
      </div>

      <div style={leftPaneStyle}>

        <div style={{ width: '100%', maxWidth: '420px' }}>
        <div style={cardStyle}>
        <div style={cardBodyStyle}>
          {/* Logo — inside the card */}
          <div style={{...paneLogoStyle, marginBottom: '24px'}}>
            <p style={logoWordmarkStyle}>Prosperity</p>
            <p style={logoSubStyle}>Capital Advisors</p>
            <hr style={logoDividerStyle} />
            <p style={taglineStyle}>Comprehensive Wealth Management. Simplified.</p>
          </div>

          {(isError || errorStatusCode) && (
            <div className="mb-3">
              <LoginError errorStatusCode={errorStatusCode} />
            </div>
          )}

          {isLoading && (
            <div className="text-center my-3">
              <div className="spinner-border" role="status" style={{color: brand.tealBlue}}>
                <span className="visually-hidden" />
              </div>
            </div>
          )}

          {/* SSO login */}
          {!show2FA && !show2FAlogin && (
            <>
              {!isLoading && (
                <button
                  style={googleBtnStyle}
                  onClick={loginWithMicrosoft}
                  disabled={!canLogin}
                >
                  Log In With Prosperity SSO
                </button>
              )}
            </>
          )}

          {/* 2FA setup flow */}
          {show2FA && (
            <div className="text-center">
              <p style={{color: brand.charcoal, fontSize: '13px', marginBottom: '4px'}}>Signed in as</p>
              <p style={{color: brand.deepBlue, fontWeight: '700', marginBottom: '16px'}}>{username}</p>
              {!qrCode ? (
                <>
                  <p style={{color: brand.charcoal, fontSize: '14px', marginBottom: '16px'}}>
                    Two-Factor Authentication is required for your account.
                  </p>
                  <button style={googleBtnStyle} onClick={() => enable2FA()}>
                    Set Up 2FA
                  </button>
                </>
              ) : (
                <>
                  <img src={qrCode.qr_code} alt="2FA QR Code" style={{maxWidth: '220px', marginBottom: '16px', borderRadius: '4px'}} />
                  <InputGroup className="mb-3">
                    <Button
                      style={{backgroundColor: brand.charcoal, borderColor: brand.charcoal}}
                      onClick={() => handleCopy(qrCode.uri)}
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </Button>
                    <Form.Control type="text" value={qrCode.uri} disabled readOnly />
                  </InputGroup>
                  <Alert style={{backgroundColor: '#e8f3fa', borderColor: brand.tealBlue, color: brand.deepBlue, fontSize: '13px'}}>
                    Scan the QR code with your authenticator app, then enter the 6-digit code below.
                  </Alert>
                  <TwoFAInputs />
                  {invalid2FA && (
                    <Alert variant="danger" className="mt-3" style={{fontSize: '13px'}}>
                      INVALID AUTHENTICATOR CODE
                    </Alert>
                  )}
                </>
              )}
            </div>
          )}

          {/* 2FA login flow */}
          {show2FAlogin && (
            <div className="text-center">
              <p style={{color: brand.charcoal, fontSize: '13px', marginBottom: '4px'}}>Signed in as</p>
              <p style={{color: brand.deepBlue, fontWeight: '700', marginBottom: '8px'}}>{username}</p>
              <p style={{color: brand.charcoal, fontSize: '14px', marginBottom: '4px'}}>
                Enter your 6-digit authenticator code.
              </p>
              <TwoFAInputs />
              {invalid2FA && (
                <Alert variant="danger" className="mt-3" style={{fontSize: '13px'}}>
                  INVALID AUTHENTICATOR CODE
                </Alert>
              )}
            </div>
          )}
        </div>
        </div>

        {/* Email-migration help — secondary button below the white card */}
        <button
          type="button"
          onClick={() => navigate('/email-help')}
          style={{
            marginTop: '36px',
            background: '#ffffff',
            border: '1px solid #0069a6',
            color: '#0069a6',
            fontSize: '13px',
            fontWeight: 700,
            letterSpacing: '0.5px',
            cursor: 'pointer',
            padding: '10px 18px',
            borderRadius: '6px',
            display: 'block',
            marginLeft: 'auto',
            marginRight: 'auto',
            transition: 'background-color 0.15s, color 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#e9e6e1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#ffffff';
          }}
        >
          <i className="bi bi-envelope-paper me-2" />
          Need help with your Prosperity Email Address?
        </button>

        {/* Other sign-in surfaces (clients + B2B/entrepreneur firms). Each link
            is gated by an admin config flag; the whole block hides when neither
            is enabled. */}
        {(showClientPortalLink || showEntrepreneurPortalLink) && (
        <div style={{ marginTop: '32px', textAlign: 'center' }}>
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 10px' }}>
            Not a Prosperity employee?
          </p>
          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            {showEntrepreneurPortalLink && (
              <button type="button" onClick={() => navigate('/entrepreneur/login')} style={footerLinkStyle}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#ffffff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}>
                Entrepreneurs
              </button>
            )}
            {showEntrepreneurPortalLink && showClientPortalLink && (
              <span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
            )}
            {showClientPortalLink && (
              <button type="button" onClick={() => navigate('/portal/login')} style={footerLinkStyle}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#ffffff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}>
                Client Portal
              </button>
            )}
          </div>
        </div>
        )}
        </div>
      </div>
    </div>
  );
}
